import { ArrowForwardIosSharp } from '@mui/icons-material';
import {
    Accordion as MuiAccordion,
    AccordionDetails as MuiAccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    AccordionSummaryProps,
    Alert,
    Box,
    Chip,
    LinearProgress,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import { AccordionProps } from '@mui/material/Accordion/Accordion';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { PersonReferenceDTO } from '../../api/dto';
import { addTaskAssignees, TaskPreciseDTO, updateTimeoutEscalation, updateTimeoutReminder } from '../../api/task';
import { HttpCode } from '../../api/types';
import { isApiError } from '../../api/utils';
import { artificialDelay } from '../../lib/delay';
import { translateApiError } from '../../lib/language';
import { DocumentDrawerContext } from '../documents/DocumentDrawerStructure';
import { TaskCard } from './TaskCard';
import { useTaskIsImportant } from './useTaskIsImportant';
import { useTasks } from './useTasks';

// Customization taken from https://mui.com/material-ui/react-accordion/#customization

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
    ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:before': {
            display: 'none',
        },
    })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, .03)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
        transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
        marginLeft: theme.spacing(1),
    },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface TaskHistoryProps {
    tasks: ReturnType<typeof useTasks>;
    disabled: boolean;
    withUnsavedChangesPrompt: (f: () => void) => void;
    onTaskChange: () => void;
    onTaskUpdate: () => void;
}

export const TaskHistory: React.FC<TaskHistoryProps> = ({
    tasks: tasksData,
    disabled,
    withUnsavedChangesPrompt,
    onTaskChange,
    onTaskUpdate,
}) => {
    const intl = useIntl();
    const { isLoading, isError, assignableTasks, otherTasks, completedTasks, cancelledTasks, currentTask } = tasksData;

    const { setActiveTasksCount, setTaskSelected } = useContext(DocumentDrawerContext)!;

    const taskErrorHandler = (error: any) => {
        if (isApiError(error) && (error?.status === HttpCode.CONFLICT || error?.status === HttpCode.BAD_REQUEST)) {
            const validationError = translateApiError(error.data, intl.locale);
            toast.warning(validationError.message);
        } else {
            toast.warning(intl.formatMessage({ id: 'document.error.conflict' }));
        }
    };

    const { mutate: mutateAddAssignees, isLoading: isAddAssigneesLoading } = useMutation(
        (data: { taskId: string; newAssignees: PersonReferenceDTO[]; comment: string | null }) =>
            addTaskAssignees({
                taskId: data.taskId,
                newAssignees: data.newAssignees,
                comment: { comment: data.comment },
            }).then(artificialDelay),
        {
            onSuccess: onTaskUpdate,
            onError: taskErrorHandler,
        }
    );
    const addIndividualAssignees =
        ({ task }: { task: TaskPreciseDTO }) =>
        ({ newAssignees, comment }: { newAssignees: PersonReferenceDTO[]; comment: string | null }) =>
            mutateAddAssignees({
                taskId: task.id,
                newAssignees,
                comment,
            });

    const { mutate: mutateUpdateTimeout, isLoading: isUpdateTimeoutLoading } = useMutation(
        ({ taskId, date, isEscalation }: { taskId: string; date: Date | null; isEscalation: boolean }) =>
            (isEscalation ? updateTimeoutEscalation : updateTimeoutReminder)({
                taskId,
                date,
            }).then(artificialDelay),
        {
            onSuccess: onTaskUpdate,
            onError: taskErrorHandler,
        }
    );
    const updateTimeout =
        ({ task }: { task: TaskPreciseDTO }) =>
        (data: { date: Date | null; isEscalation: boolean }) =>
            mutateUpdateTimeout({
                taskId: task.id,
                ...data,
            });

    useEffect(() => {
        // Behaves weirdly if there is more than one page (shouldn't happen)
        setActiveTasksCount(assignableTasks.length);
    }, [setActiveTasksCount, assignableTasks]);
    useEffect(() => {
        setTaskSelected(currentTask);
    }, [setTaskSelected, currentTask]);

    const [selectedGroup, setSelectedGroup] = useState(0);

    const categories = [
        {
            label: intl.formatMessage({ id: 'document.tasks.myActive' }),
            content: assignableTasks,
        },
        { label: intl.formatMessage({ id: 'document.tasks.otherActive' }), content: otherTasks },
        { label: intl.formatMessage({ id: 'document.tasks.completed' }), content: completedTasks },
        { label: intl.formatMessage({ id: 'document.tasks.closed' }), content: cancelledTasks },
    ].map((o) => ({ ...o, isCurrent: o.content.some(({ id }) => id === currentTask?.id) }));

    const isTaskImportant = useTaskIsImportant();
    const existsImportantTask = assignableTasks.some(isTaskImportant);

    return (
        <>
            {isLoading ? (
                <LinearProgress />
            ) : isError ? (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="error">
                        <FormattedMessage id="document.tasks.error" />
                    </Alert>
                </Box>
            ) : categories.every((task) => task.content.length === 0) ? (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="info">
                        <FormattedMessage id="document.tasks.empty" />
                    </Alert>
                </Box>
            ) : (
                <Box>
                    {categories
                        .filter(({ content }) => content.length > 0)
                        .map(({ label, content, isCurrent }, i) => (
                            <Accordion key={i} expanded={selectedGroup === i} onChange={() => setSelectedGroup(i)}>
                                <AccordionSummary>
                                    <Stack direction="row" spacing={1}>
                                        <Typography>{label}</Typography>
                                        <Chip
                                            label={content.length}
                                            color={isCurrent ? 'info' : undefined}
                                            size="small"
                                            sx={{ cursor: 'inherit' }}
                                        />
                                    </Stack>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack direction="column" spacing={2}>
                                        {content.map((task) => (
                                            <TaskCard
                                                key={task.id}
                                                task={task}
                                                tasks={tasksData}
                                                addIndividualAssignees={addIndividualAssignees}
                                                updateTimeout={updateTimeout}
                                                isLoading={isAddAssigneesLoading || isUpdateTimeoutLoading}
                                                disabled={disabled}
                                                withUnsavedChangesPrompt={withUnsavedChangesPrompt}
                                                onTaskChange={onTaskChange}
                                                unselectable={!existsImportantTask}
                                            />
                                        ))}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </Box>
            )}
        </>
    );
};
